// src/pages/admin/adminpanel/InquiriesPanel.jsx
import React, { useState } from 'react';

const statusStyle = (s) => ({
  replied:  'bg-emerald-100 text-emerald-700',
  pending:  'bg-amber-100 text-amber-700',
  open:     'bg-blue-100 text-blue-700',
  closed:   'bg-gray-100 text-gray-600',
}[s] || 'bg-gray-100 text-gray-600');

const STATUS_FILTERS = ['all', 'pending', 'replied', 'open', 'closed'];

function usePagination(data, perPage) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PerPageSelector({ perPage, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-secondary">Rows</span>
      <div className="flex gap-1">
        {[5, 10, 20, 50].map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
              ${perPage === n
                ? 'bg-primary text-white'
                : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-3">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

// ── Helper: resolve the subject name of an inquiry ──
// Animal inquiries → animal_name, Meat product inquiries → product_name
function subjectName(q) {
  if (q.animal_name) return { label: q.animal_name, tag: 'Animal' };
  if (q.product_name) return { label: q.product_name, tag: 'Meat' };
  return { label: '—', tag: null };
}

export default function InquiriesPanel({ inquiries, loading }) {
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const [perPage,  setPerPage]  = useState(5);

  const filtered = inquiries
    .filter(q => filter === 'all' || (q.status || 'open') === filter)
    .filter(q =>
      !search ||
      q.animal_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.buyer_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.sender_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.seller_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.message?.toLowerCase().includes(search.toLowerCase())
    );

  const pagination = usePagination(filtered, perPage);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { pagination.reset(); }, [filter, search, perPage]);

  const handlePerPageChange = (n) => { setPerPage(n); pagination.reset(); };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">

      {/* ── Header ── */}
      <div className="p-6 border-b border-surface-container-high
                      flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="font-bold text-lg">All Inquiries</h3>
          <p className="text-xs text-secondary mt-0.5">
            {filtered.length} of {inquiries.length} inquiries
          </p>
        </div>
        <div className="flex items-center gap-2 bg-surface-container-low px-3 py-2
                        rounded-xl border border-outline-variant/20">
          <span className="material-symbols-outlined text-outline text-sm">search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search animal, product, buyer, seller, message…"
            className="bg-transparent outline-none text-sm w-56
                       placeholder:text-outline-variant"
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="text-outline hover:text-on-surface transition-colors">
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Status filter pills ── */}
      <div className="px-6 py-3 flex gap-2 flex-wrap border-b
                      border-surface-container-high bg-surface-container-low/50">
        {STATUS_FILTERS.map((s) => {
          const count = s === 'all'
            ? inquiries.length
            : inquiries.filter(q => (q.status || 'open') === s).length;
          return (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all
                          ${filter === s
                            ? 'bg-primary text-white shadow-sm'
                            : `${statusStyle(s === 'all' ? null : s)}
                               ${s === 'all' ? 'bg-gray-100 text-gray-600' : ''}
                               hover:opacity-80`}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
              <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* ── Loading ── */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary
                          border-t-transparent rounded-full animate-spin" />
        </div>

      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-secondary">
          <span className="material-symbols-outlined text-4xl text-outline-variant">forum</span>
          <p className="mt-2 font-semibold">
            {inquiries.length === 0 ? 'No inquiries yet' : 'No results found'}
          </p>
          {search && <p className="text-xs mt-1">Try a different search term</p>}
        </div>

      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-surface-container">
            {pagination.slice.map((q) => {
              const status = q.status || 'open';
              const isOpen = expanded === q.id;
              const { label, tag } = subjectName(q);
              return (
                <div key={q.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold">{label}</p>
                        {tag && (
                          <span className="text-[10px] bg-amber-100 text-amber-700
                                           px-1.5 py-0.5 rounded font-bold">{tag}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-outline font-mono">#{q.id}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px]
                                      font-bold flex-shrink-0 ${statusStyle(status)}`}>
                      {status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-secondary">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center
                                      justify-center text-blue-700 text-[9px] font-black">
                        {(q.buyer_name || q.sender_name || '?')[0].toUpperCase()}
                      </div>
                      {q.buyer_name || q.sender_name || '—'}
                    </div>
                    <span className="text-outline">→</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center
                                      justify-center text-emerald-700 text-[9px] font-black">
                        {(q.seller_name || '?')[0].toUpperCase()}
                      </div>
                      {q.seller_name || '—'}
                    </div>
                  </div>
                  <p className={`text-xs text-secondary ${isOpen ? '' : 'line-clamp-2'}`}>
                    {q.message || '—'}
                  </p>
                  {q.message && (
                    <button onClick={() => setExpanded(isOpen ? null : q.id)}
                      className="text-[11px] font-bold text-primary">
                      {isOpen ? 'Show less ↑' : 'Show more ↓'}
                    </button>
                  )}
                  {isOpen && q.reply && (
                    <div className="pl-3 border-l-2 border-emerald-300 mt-1">
                      <p className="text-[10px] font-black uppercase tracking-widest
                                    text-emerald-600 mb-1">Seller Reply</p>
                      <p className="text-xs text-on-surface">{q.reply}</p>
                    </div>
                  )}
                  <p className="text-[10px] text-outline">
                    {q.created_at ? new Date(q.created_at).toLocaleDateString('en-IN') : '—'}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-surface-container-low">
                <tr>
                  {['ID', 'Subject', 'From', 'To Seller', 'Message', 'Status', 'Date', ''].map(h => (
                    <th key={h}
                      className="px-5 py-3 text-[10px] font-black uppercase
                                 tracking-widest text-secondary whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container">
                {pagination.slice.map((q) => {
                  const status = q.status || 'open';
                  const isOpen = expanded === q.id;
                  const { label, tag } = subjectName(q);
                  return (
                    <React.Fragment key={q.id}>
                      <tr className="hover:bg-surface-container-low/50 transition-colors">
                        <td className="px-5 py-3 text-xs font-mono text-secondary">#{q.id}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold whitespace-nowrap">{label}</p>
                            {tag && (
                              <span className="text-[10px] bg-amber-100 text-amber-700
                                               px-1.5 py-0.5 rounded font-bold shrink-0">
                                {tag}
                              </span>
                            )}
                          </div>
                          {q.category && (
                            <p className="text-[10px] text-secondary">{q.category}</p>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center
                                            justify-center text-blue-700 text-[10px] font-black
                                            flex-shrink-0">
                              {(q.buyer_name || q.sender_name || '?')[0].toUpperCase()}
                            </div>
                            <span className="text-sm text-secondary whitespace-nowrap">
                              {q.buyer_name || q.sender_name || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center
                                            justify-center text-emerald-700 text-[10px] font-black
                                            flex-shrink-0">
                              {(q.seller_name || '?')[0].toUpperCase()}
                            </div>
                            <span className="text-sm text-secondary whitespace-nowrap">
                              {q.seller_name || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-sm text-secondary max-w-[200px]">
                          <p className={`${isOpen ? '' : 'truncate'}`}>{q.message || '—'}</p>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-[11px]
                                            font-bold ${statusStyle(status)}`}>
                            {status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs text-secondary whitespace-nowrap">
                          {q.created_at
                            ? new Date(q.created_at).toLocaleDateString('en-IN')
                            : '—'}
                        </td>
                        <td className="px-5 py-3">
                          {q.message && (
                            <button
                              onClick={() => setExpanded(isOpen ? null : q.id)}
                              className="p-1 rounded-full hover:bg-surface-container
                                         text-outline hover:text-on-surface transition-all">
                              <span className="material-symbols-outlined text-[18px]">
                                {isOpen ? 'expand_less' : 'expand_more'}
                              </span>
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-surface-container-low/40">
                          <td colSpan={8} className="px-5 py-4">
                            <div className="flex items-start gap-3">
                              <span className="material-symbols-outlined text-outline text-[20px] mt-0.5">
                                chat_bubble
                              </span>
                              <div>
                                <p className="text-[10px] font-black uppercase tracking-widest
                                              text-secondary mb-1">Full Message</p>
                                <p className="text-sm text-on-surface leading-relaxed">{q.message}</p>
                                {q.reply && (
                                  <div className="mt-3 pl-4 border-l-2 border-emerald-300">
                                    <p className="text-[10px] font-black uppercase tracking-widest
                                                  text-emerald-600 mb-1">Seller Reply</p>
                                    <p className="text-sm text-on-surface leading-relaxed">{q.reply}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Footer ── */}
      {filtered.length > 0 && (
        <div className="px-6 py-4 border-t border-surface-container-high
                        bg-surface-container-low/50 space-y-3">
          <div className="flex items-center justify-between">
            <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
            <div className="text-right">
              <p className="text-xs text-secondary">
                {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, filtered.length)} of {filtered.length}
              </p>
              <p className="text-xs text-secondary mt-0.5">
                <span className="font-bold text-emerald-600">
                  {inquiries.filter(q => (q.status || 'open') === 'replied').length}
                </span> replied ·{' '}
                <span className="font-bold text-amber-600">
                  {inquiries.filter(q => (q.status || 'open') === 'pending').length}
                </span> pending
              </p>
            </div>
          </div>
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.totalPages}
            setPage={pagination.setPage}
          />
        </div>
      )}

    </div>
  );
}